<template>
  <div class="section-edit-container">
    <div v-if="isLoading" class="loading-state">
      <div class="loading-shimmer"></div>
    </div>

    <div v-else-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="fetchSectionDetail" class="retry-button">Retry</button>
    </div>

    <div v-else class="section-form-wrapper">
      <form @submit.prevent="updateSection" class="section-form">
        <div class="form-group">
          <label for="title">Title</label>
          <input 
            id="title"
            v-model="formData.title"
            type="text"
            required
            placeholder="e.g., Homepage Banner"
          >
        </div>

        <div class="form-group">
          <label for="title_ar">Title (Arabic)</label>
          <input 
            id="title_ar"
            v-model="formData.title_ar"
            type="text"
            placeholder="Arabic title"
            class="rtl-input"
            dir="rtl"
          >
        </div>

        <div class="form-group">
          <label for="title_key">Title Key</label>
          <input 
            id="title_key"
            v-model="formData.title_key"
            type="text"
            placeholder="e.g., homepage_banner"
          >
        </div>

        <div class="form-group">
          <label for="title_key_ar">Title Key (Arabic)</label>
          <input 
            id="title_key_ar"
            v-model="formData.title_key_ar"
            type="text"
            placeholder="Arabic title key"
          >
        </div>

        <div class="form-group">
          <label for="subtitle">Subtitle</label>
          <input 
            id="subtitle"
            v-model="formData.subtitle"
            type="text"
            placeholder="e.g., Welcome to our website"
          >
        </div>

        <div class="form-group">
          <label for="subtitle_ar">Subtitle (Arabic)</label>
          <input 
            id="subtitle_ar"
            v-model="formData.subtitle_ar"
            type="text"
            placeholder="Arabic subtitle"
            class="rtl-input"
            dir="rtl"
          >
        </div>

        <div class="form-group">
          <label for="subtitle_key">Subtitle Key</label>
          <input 
            id="subtitle_key"
            v-model="formData.subtitle_key"
            type="text"
            placeholder="e.g., welcome_text"
          >
        </div>

        <div class="form-group">
          <label for="type">Section Type</label>
          <select id="type" v-model="formData.type" required>
            <option value="Banner">Banner</option>
            <option value="banner">banner</option>
            <option value="FullCardCarousel">Full Card Carousel</option>
            <option value="SquareCardCarouselV1">Square Card Carousel V1</option>
            <option value="SquareCardCarouselV2">Square Card Carousel V2</option>
            <option value="TallCardCarousel">Tall Card Carousel</option>
            <option value="GridCarousel">Grid Carousel</option>
          </select>
        </div>

        <div class="form-group">
          <label for="method_name">Method Name</label>
          <input 
            id="method_name"
            v-model="formData.method_name"
            type="text"
            required
            placeholder="e.g., fetchBannerData"
          >
        </div>

        <div class="form-group">
          <label for="cache_key">Cache Key</label>
          <input 
            id="cache_key"
            v-model="formData.cache_key"
            type="text"
            required
            placeholder="e.g., homepage_banner_cache1"
          >
        </div>

        <div class="form-group">
          <label for="ttl">Cache TTL (seconds)</label>
          <input 
            id="ttl"
            v-model.number="formData.ttl"
            type="number"
            required
            min="0"
            placeholder="e.g., 3600"
          >
        </div>

        <div class="form-group">
          <label for="order">Display Order</label>
          <input 
            id="order"
            v-model.number="formData.order"
            type="number"
            required
            min="0"
            placeholder="e.g., 1"
          >
        </div>

        <div class="form-group">
          <label class="checkbox-label">
            <input 
              type="checkbox"
              v-model="formData.is_active"
            >
            Active Section
          </label>
        </div>

        <div class="form-actions">
          <button type="button" class="cancel-button" @click="cancel">Cancel</button>
          <button type="submit" class="submit-button" :disabled="isSubmitting">
            {{ isSubmitting ? 'Updating...' : 'Update Section' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

export default {
  name: 'HomepageSectionEdit',
  props: {
    sectionId: {
      type: [Number, String],
      required: true
    }
  },
  emits: ['section-updated'],
  setup(props, { emit }) {
    const router = useRouter();
    const isLoading = ref(true);
    const isSubmitting = ref(false);
    const error = ref(null);
    
    const formData = reactive({
      title: '',
      title_ar: '',
      title_key: '',
      title_key_ar: '',
      subtitle: '',
      subtitle_ar: '',
      subtitle_key: '',
      type: '',
      method_name: '',
      cache_key: '',
      ttl: 3600,
      order: 0,
      is_active: true
    });

    const fetchSectionDetail = async () => {
      isLoading.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token') ;
        
        const response = await axios.get(`https://api.kewaar.com/api/admin/homepage-sections/${props.sectionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        if (response.data && response.data.success) {
          const section = response.data.data;
          
          // Populate form data with section details
          Object.keys(formData).forEach(key => {
            if (section[key] !== undefined) {
              formData[key] = section[key];
            }
          });
        } else {
          error.value = 'Failed to load section details: ' + (response.data.message || 'Unknown error');
        }
      } catch (err) {
        console.error('Error fetching section details:', err);
        error.value = 'Failed to load section details: ' + (err.message || 'Network error');
      } finally {
        isLoading.value = false;
      }
    };

    const updateSection = async () => {
      isSubmitting.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token') ;
        
        const response = await axios.put(
          `https://api.kewaar.com/api/admin/homepage-sections/${props.sectionId}`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );
        
        if (response.data && response.data.success) {
          emit('section-updated', response.data.data);
          router.push(`/homepage/sections/${props.sectionId}`);
        } else {
          error.value = 'Failed to update section: ' + (response.data.message || 'Unknown error');
        }
      } catch (err) {
        console.error('Error updating section:', err);
        error.value = 'Failed to update section: ' + (err.message || 'Network error');
      } finally {
        isSubmitting.value = false;
      }
    };

    const cancel = () => {
      router.push(`/homepage/sections/${props.sectionId}`);
    };

    onMounted(() => {
      fetchSectionDetail();
    });

    return {
      formData,
      isLoading,
      isSubmitting,
      error,
      fetchSectionDetail,
      updateSection,
      cancel
    };
  }
};
</script>

<style scoped>
.section-edit-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading-state {
  height: 400px;
  background: #f1f5f9;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.loading-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.5s infinite;
  transform: translateX(-100%);
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

.error-message {
  background: #FEE2E2;
  color: #B91C1C;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
}

.retry-button {
  background: #DC2626;
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 6px;
  margin-top: 1rem;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: #B91C1C;
}

.section-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4B5563;
  margin-bottom: 0.5rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.75rem;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1F2937;
  background: #F9FAFB;
  transition: all 0.2s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background: white;
}

.rtl-input {
  text-align: right;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
}

.form-actions {
  grid-column: span 2;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.cancel-button,
.submit-button {
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.cancel-button {
  background: #F3F4F6;
  color: #4B5563;
}

.cancel-button:hover {
  background: #E5E7EB;
}

.submit-button {
  background: #3B82F6;
  color: white;
}

.submit-button:hover:not(:disabled) {
  background: #2563EB;
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .section-edit-container {
    padding: 1.5rem;
  }
  
  .section-form {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    grid-column: span 1;
  }
}
</style> 